import { useCallback, useEffect, useState } from "react";
import { Search, Briefcase } from "lucide-react";
import { JobRow } from "@/components/JobRow";
import { PostJobDialog } from "@/components/PostJobDialog";
import { JobDetailDialog } from "@/components/JobDetailDialog";
import { Spinner, EmptyState } from "@/components/Loader";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { isProfessional } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const SOURCES = { browse: "/jobs", leads: "/leads", mine: "/jobs/mine" };

export default function Jobs() {
  const { user } = useAuth();
  const [tab, setTab] = useState("browse");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    setItems(null);
    api.get(SOURCES[tab]).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const needle = q.toLowerCase();
  const shown = (items || []).filter((j) => !needle || [j.title, j.description, j.city, j.category].some((v) => v?.toLowerCase().includes(needle)));

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="tm-eyebrow">Jobs & leads</p><h1 className="mt-2 font-display text-2xl font-extrabold text-tm-green sm:text-3xl">Work for tax professionals</h1></div>
        {user && isProfessional(user) && <PostJobDialog onCreated={() => (tab === "browse" ? load() : setTab("browse"))} />}
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="border bg-white">
          <TabsTrigger value="browse" data-testid="jobs-tab-browse">Jobs</TabsTrigger>
          <TabsTrigger value="leads" data-testid="jobs-tab-leads">Client leads</TabsTrigger>
          {user && <TabsTrigger value="mine" data-testid="jobs-tab-mine">My jobs</TabsTrigger>}
        </TabsList>
      </Tabs>
      <div className="relative mt-5"><Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, city or category" className="h-11 rounded-full bg-white pl-10" data-testid="jobs-search-input" /></div>
      <div className="mt-6 space-y-3">
        {items === null ? <Spinner /> : shown.length === 0 ? <EmptyState icon={Briefcase} title="Nothing here yet" text={tab === "browse" ? "No open jobs right now. Post one!" : "Try another tab."} testid="jobs-empty" />
          : shown.map((j) => <JobRow key={j.id} job={j} onOpen={setOpen} />)}
      </div>
      <JobDetailDialog job={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
